import {
  canonicalizeTraits,
  evaluateWithHistory,
  findChip,
} from './evaluate.ts'
import type {
  AnomalyScenario,
  GameAction,
  GameState,
  MetaphorSelection,
  StabilizationRecord,
  Trait,
} from './model.ts'
import { SCENARIOS } from './scenarios.ts'

export const INITIAL_INTEGRITY = 2

export function createInitialGameState(
  selection: MetaphorSelection = {},
): GameState {
  return {
    version: 1,
    status: 'playing',
    roundIndex: 0,
    integrity: INITIAL_INTEGRITY,
    selection: { ...selection },
    lastEvaluation: null,
    attempts: [],
    successes: [],
  }
}

export function getCurrentScenario(state: GameState): AnomalyScenario | null {
  return SCENARIOS[state.roundIndex] ?? null
}

export function selectChip(slotId: string, chipId: string): GameAction {
  return { type: 'SELECT_CHIP', slotId, chipId }
}

export function clearSlot(slotId: string): GameAction {
  return { type: 'CLEAR_SLOT', slotId }
}

export function resolveInterpretation(
  traits: readonly Trait[],
  metaphor: string,
): GameAction {
  return { type: 'RESOLVE_INTERPRETATION', traits, metaphor }
}

function reduceSelection(state: GameState, action: GameAction): GameState {
  const scenario = getCurrentScenario(state)
  if (scenario === null) {
    return state
  }

  if (action.type === 'SELECT_CHIP') {
    if (findChip(scenario, action.slotId, action.chipId) === undefined) {
      return state
    }
    if (
      state.selection[action.slotId] === action.chipId &&
      state.lastEvaluation === null
    ) {
      return state
    }

    return {
      ...state,
      selection: {
        ...state.selection,
        [action.slotId]: action.chipId,
      },
      lastEvaluation: null,
    }
  }

  if (action.type === 'CLEAR_SLOT') {
    if (state.selection[action.slotId] === undefined) {
      return state
    }

    const selection: Record<string, string> = { ...state.selection }
    delete selection[action.slotId]
    return {
      ...state,
      selection,
      lastEvaluation: null,
    }
  }

  return state
}

function stabilizationRecord(
  state: GameState,
  evaluation: NonNullable<GameState['lastEvaluation']>,
): StabilizationRecord {
  return {
    scenarioId: evaluation.scenarioId,
    roundIndex: state.roundIndex,
    selection: { ...evaluation.selection },
    sentence: evaluation.sentence,
    interpretedTraits: [...evaluation.interpretedTraits],
    inheritedTraits: [...evaluation.inheritedTraits],
    effectiveTraits: [...evaluation.effectiveTraits],
  }
}

function resolve(
  state: GameState,
  rawTraits: readonly unknown[],
  metaphor: string,
): GameState {
  const scenario = getCurrentScenario(state)
  if (scenario === null) {
    return state
  }

  const evaluation = evaluateWithHistory(
    scenario,
    state.selection,
    canonicalizeTraits(rawTraits),
    metaphor,
    state.successes,
  )
  if (!evaluation.complete) {
    return state
  }

  const attempts = [
    ...state.attempts,
    {
      attemptIndex: state.attempts.length,
      roundIndex: state.roundIndex,
      evaluation,
    },
  ]

  if (evaluation.stable) {
    const successes = [...state.successes, stabilizationRecord(state, evaluation)]
    const completedGame = state.roundIndex === SCENARIOS.length - 1

    return {
      ...state,
      status: completedGame ? 'won' : 'playing',
      roundIndex: completedGame ? SCENARIOS.length : state.roundIndex + 1,
      selection: {},
      lastEvaluation: evaluation,
      attempts,
      successes,
    }
  }

  const integrity = state.integrity - 1
  return {
    ...state,
    status: integrity === 0 ? 'lost' : 'playing',
    integrity,
    lastEvaluation: evaluation,
    attempts,
  }
}

export function gameReducer(state: GameState, action: GameAction): GameState {
  if (state.status !== 'playing') {
    return state
  }

  if (action.type === 'RESOLVE_INTERPRETATION') {
    const rawTraits: readonly unknown[] = Array.isArray(action.traits)
      ? action.traits
      : []
    const metaphor = typeof action.metaphor === 'string' ? action.metaphor : ''
    return resolve(state, rawTraits, metaphor)
  }

  return reduceSelection(state, action)
}

export const reduceGame = gameReducer

export function replayGame(
  actions: readonly GameAction[],
  initialState: GameState = createInitialGameState(),
): GameState {
  return actions.reduce(gameReducer, initialState)
}

export function actionsForSelection(
  scenario: AnomalyScenario,
  selection: MetaphorSelection,
): readonly GameAction[] {
  const actions: GameAction[] = []
  for (const slot of scenario.slots) {
    const chipId = selection[slot.id]
    if (chipId !== undefined) {
      actions.push(selectChip(slot.id, chipId))
    }
  }
  return actions
}

export function actionsForInterpretation(
  scenario: AnomalyScenario,
  selection: MetaphorSelection,
  traits: readonly Trait[],
  metaphor: string,
): readonly GameAction[] {
  return [
    ...actionsForSelection(scenario, selection),
    resolveInterpretation(traits, metaphor),
  ]
}
